// src/component/datatable2/hooks/useHorizontalOverflow.ts

import { type RefObject, useEffect, useState } from 'react';

export interface HorizontalOverflow {
    /** The element is wider than its visible area. */
    overflowing: boolean;
    /** Columns are hidden on the left (scrolled away from the start). */
    hiddenStart: boolean;
    /** Columns are hidden on the right (not scrolled to the end). */
    hiddenEnd: boolean;
}

const NONE: HorizontalOverflow = { overflowing: false, hiddenStart: false, hiddenEnd: false };

/** Within this many pixels of an edge counts as "at the edge" (sub-pixel rounding). */
const EDGE_TOLERANCE = 1;

/** Horizontal overflow of a scroll container (scrollWidth / clientWidth / scrollLeft). */
export function measureHorizontalOverflow(element: Pick<HTMLElement, 'scrollWidth' | 'clientWidth' | 'scrollLeft'>): HorizontalOverflow {
    const overflowing = element.scrollWidth - element.clientWidth > EDGE_TOLERANCE;

    if (!overflowing) {
        return NONE;
    }

    return {
        overflowing,
        hiddenStart: element.scrollLeft > EDGE_TOLERANCE,
        hiddenEnd: element.scrollLeft + element.clientWidth < element.scrollWidth - EDGE_TOLERANCE,
    };
}

const same = (a: HorizontalOverflow, b: HorizontalOverflow) =>
    a.overflowing === b.overflowing && a.hiddenStart === b.hiddenStart && a.hiddenEnd === b.hiddenEnd;

/**
 * Tracks whether the element matched by `selector` inside `containerRef`
 * scrolls horizontally, and which edges hide content. Re-measured on scroll,
 * on resize (ResizeObserver, when available) and whenever `deps` change.
 */
export function useHorizontalOverflow(
    containerRef: RefObject<HTMLElement | null>,
    selector: string,
    deps: readonly unknown[] = [],
): HorizontalOverflow {
    const [state, setState] = useState<HorizontalOverflow>(NONE);

    useEffect(() => {
        const element = containerRef.current?.querySelector<HTMLElement>(selector);

        if (!element) {
            setState(NONE);
            return undefined;
        }

        const update = () => {
            const next = measureHorizontalOverflow(element);

            setState(previous => (same(previous, next) ? previous : next));
        };

        update();
        element.addEventListener('scroll', update, { passive: true });

        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);

        observer?.observe(element);

        const table = element.firstElementChild;

        if (table) {
            observer?.observe(table);
        }

        return () => {
            element.removeEventListener('scroll', update);
            observer?.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [containerRef, selector, ...deps]);

    return state;
}
